<template>
  <div>
    <eg-button
      :id="tooltipId"
      :type="buttonType"
      :color="buttonColor"
      :icon="buttonIcon"
      :disabled="disabled"
      :is-loading="isLoading"
      size="medium"
      @click="handleClick"
    >
      <template v-if="showPlatformIcon" #icon>
        <img :src="platformIcon" :alt="platformLabel" class="platform-icon" />
      </template>
      {{ buttonText }}
    </eg-button>

    <eg-b-tooltip
      v-if="showTooltip"
      :target="tooltipId"
      triggers="hover"
      placement="top"
    >
      {{ tooltipText }}
    </eg-b-tooltip>
  </div>
</template>

<script>
import { updateEssay } from "@/api";
import { LMS_PROVIDERS } from "@/constants";

export default {
  name: "EssayReviewButton",

  props: {
    essay: {
      type: Object,
      required: true,
    },

    /**
     * Current essay status
     * @values 'Graded', 'Reviewed', 'Exported'
     */
    status: {
      type: String,
      required: true,
      validator: (value) => ["Graded", "Reviewed", "Exported"].includes(value),
    },

    /**
     * Unique identifier for tooltip targeting
     */
    id: {
      type: String,
      default: () =>
        `essay-review-btn-${Math.random().toString(36).substr(2, 9)}`,
    },

    /**
     * Whether the button is disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether to show tooltip
     */
    showTooltip: {
      type: Boolean,
      default: true,
    },
  },

  emits: [
    /**
     * Emitted when status change is successful
     * @param {Object} navigationHint - Object with navigation suggestions and status
     */
    "statusChangeSuccess",
  ],

  data() {
    return {
      isLoading: false,
      localStatus: null, // Local status for optimistic updates
    };
  },

  computed: {
    tooltipId() {
      return this.id;
    },

    // Use local status for optimistic updates, fallback to prop
    currentStatus() {
      return this.localStatus || this.status;
    },

    isReviewed() {
      return ["Reviewed", "Exported"].includes(this.currentStatus);
    },

    buttonType() {
      return this.currentStatus === "Exported" ? "secondary" : "primary";
    },

    buttonColor() {
      switch (this.currentStatus) {
        case "Reviewed":
          return "var(--color-surface-semantic-success-default)"; // Green background for reviewed
        case "Exported":
          return "#6c757d"; // Gray background for exported
        case "Graded":
        default:
          return "#8a3ffc"; // Purple background for ready to review
      }
    },

    buttonIcon() {
      switch (this.currentStatus) {
        case "Exported":
          return "export-dots";
        case "Reviewed":
          return "check-circle";
        case "Graded":
        default:
          return "check-circle";
      }
    },

    buttonText() {
      switch (this.currentStatus) {
        case "Exported":
          return "Exported";
        case "Reviewed":
          return "Reviewed";
        case "Graded":
        default:
          return "Mark as reviewed";
      }
    },

    tooltipText() {
      switch (this.currentStatus) {
        case "Exported":
          return "Revert to Ready for Review";
        case "Reviewed":
          return "Revert to Ready for Review";
        case "Graded":
        default:
          return "Mark this submission as reviewed";
      }
    },

    showPlatformIcon() {
      return this.currentStatus === "Exported" && this.essay.folder?.platform;
    },

    platformIcon() {
      const platform = this.essay.folder?.platform;
      return this.showPlatformIcon ? LMS_PROVIDERS[platform]?.icon : null;
    },

    platformLabel() {
      const platform = this.essay.folder?.platform;
      return this.showPlatformIcon ? LMS_PROVIDERS[platform]?.label : null;
    },
  },

  watch: {
    // Reset local status when prop changes (parent component update)
    status(newStatus) {
      if (this.localStatus && this.localStatus !== newStatus) {
        this.localStatus = null;
      }
    },
    // Reset local status when essay changes (navigation to different essay)
    essay: {
      handler(newEssay, oldEssay) {
        if (oldEssay && newEssay && oldEssay.id !== newEssay.id) {
          this.localStatus = null;
        }
      },
      deep: false,
    },
  },

  methods: {
    async handleClick() {
      if (this.disabled || this.isLoading) return;

      let newStatus;
      switch (this.currentStatus) {
        case "Exported":
          // Exported essays can be un-reviewed (set back to Graded)
          newStatus = "Graded";
          break;
        case "Reviewed":
          // Un-review: set back to Graded
          newStatus = "Graded";
          break;
        case "Graded":
        default:
          // Mark as reviewed
          newStatus = "Reviewed";
          break;
      }

      await this.changeStatus(newStatus);
    },

    async changeStatus(status) {
      this.isLoading = true;

      let response = null;
      let error = null;

      // Make the API call and capture both success and error cases
      try {
        response = await updateEssay(this.essay.id, {
          status,
        });
      } catch (err) {
        error = err;
      }

      // Check if the request was successful
      if (response && response.status >= 200 && response.data) {
        // Optimistically update local status immediately
        this.localStatus = status;
        this.isLoading = false;

        // Provide navigation hints to parent component
        const navigationHint = {
          shouldNavigateNext: status !== "Graded", // Navigate to next essay unless marking as "Ready for Review"
          status,
        };

        this.$emit("statusChangeSuccess", navigationHint);
      } else {
        // Failure case - either no response or error occurred
        console.log("Status change failed:", { response, error });
        this.$showToastError("Something went wrong. Please try again.");
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.platform-icon {
  width: 16px;
  height: 16px;
  display: inline-block;
  object-fit: contain;
  margin-right: 4px;
  vertical-align: middle;
}
</style>
